/*
 * @Author: Kida
 * @Date: 2020-11-16 23:50:44
 * @LastEditTime: 2021-01-07 12:29:47
 * @LastEditors: Please set LastEditors
 * @Description: In User Settings Edit
 * @FilePath: \vr-fornt-end\src\components\modeldemo\PanoMode.js
 */

import TWEEN from 'tween.js'
import * as THREE from 'three'
import { Material } from './ModelMaterial_offscreen'
import { Camera } from './Camera'
import { Renderer } from './Renderer'
import { CPointSet } from './CameraPoints'
import { config } from './config'
import * as Utils from './Utils'

const AnimateMode = {
  ToFade: 0,
  Panorama: 1
}

export class PanoMode {
  /**
   * 构造函数
   * @param {Camera} cam 相机实例
   * @param {CPointSet} set 可用点位集实例
   * @param {number} transitionTime 动画时间
   * @param {Renderer} renderer 渲染器实例
   * @param {SingleView} singleView 单场景实例
   */
  constructor (cam, set, transitionTime, renderer, singleView) {
    /**
     * @type {number} 动画模式
     */
    this.animateMode = AnimateMode.Panorama
    /**
     * @type {Camera} 相机
     */
    this.cameraInstance = cam
    /**
     * @type {CPointSet} 点位集合
     */
    this.cpointSet = set
    /**
     * @type {Renderer} 渲染器
     */
    this.renderer = renderer

    /**
     * @type {number} 动画时间
     */
    this.transitionTime = transitionTime
    /**
     * @type {SingleView} 单场景点实例
     */
    this.singleView = singleView
  }

  /**
   * [tween] 切换动画
   * @param {number} idx 点位idx
   * @param {number} tlon 目标经度
   * @param {number} tlat 目标纬度
   * @param {number} speedRatio 速度比率
   */
  async translateTo (idx, tlon, tlat, speedRatio = 1) {
    const cam = this.cameraInstance
    const currPos = cam.camera.position
    const targetPos = this.cpointSet.getCameraPoint(idx).position

    if (tlon - cam.lon > 180) tlon -= 360
    if (tlon - cam.lon < -180) tlon += 360

    const cameraValue = {
      x1: currPos.x,
      y1: currPos.y,
      z1: currPos.z,
      fov: cam.fov
    }
    this.tween = new TWEEN.Tween(cameraValue)
    this.tween.to({
      x1: targetPos.x,
      y1: targetPos.y,
      z1: targetPos.z,
      fov: 65
    }, this.transitionTime * speedRatio)
    this.tween.onUpdate(() => {
      cam.camera.position.x = cameraValue.x1
      cam.camera.position.y = cameraValue.y1
      cam.camera.position.z = cameraValue.z1
      cam.fov = cameraValue.fov
      cam.camera.updateProjectionMatrix()
    })
    this.tween.easing(TWEEN.Easing.Sinusoidal.InOut)

    return new Promise((resolve, reject) => {
      this.tween.onComplete(() => {
        cam.cameraCurrentIndex = idx // 更新当前点位
        resolve()
      })
      this.tween.start()
    })
  }

  /**
   * [TWEEN] 切换和旋转动画
   * @param {number} idx 点位idx
   * @param {number} tlon 目标经度
   * @param {number} tlat 目标纬度
   * @param {number} speedRatio 速度比率
   */
  async translateAndRotateTo (idx, tlon, tlat, speedRatio = 1) {
    const cam = this.cameraInstance
    const currPos = cam.camera.position
    const targetPos = this.cpointSet.getCameraPoint(idx).position

    if (tlon - cam.lon > 180) tlon -= 360
    if (tlon - cam.lon < -180) tlon += 360

    const cameraValue = {
      x1: currPos.x,
      y1: currPos.y,
      z1: currPos.z,
      lon: cam.lon,
      lat: cam.lat,
      fov: cam.fov
    }
    this.tween = new TWEEN.Tween(cameraValue)
    this.tween.to({
      x1: targetPos.x,
      y1: targetPos.y,
      z1: targetPos.z,
      lon: tlon,
      lat: tlat,
      fov: 65
    }, this.transitionTime * speedRatio)
    this.tween.onUpdate(() => {
      cam.camera.position.x = cameraValue.x1
      cam.camera.position.y = cameraValue.y1
      cam.camera.position.z = cameraValue.z1
      cam.lon = cameraValue.lon
      cam.lat = cameraValue.lat
      cam.fov = cameraValue.fov
      cam.camera.updateProjectionMatrix()
    })
    this.tween.easing(TWEEN.Easing.Sinusoidal.InOut)

    return new Promise((resolve, reject) => {
      this.tween.onComplete(() => {
        cam.cameraCurrentIndex = idx // 更新当前点位
        resolve()
      })
      this.tween.start()
    })
  }

  /**
   * [TWEEN] 旋转动画
   * @param {number} tlon 目标经度
   * @param {number} tlat 目标纬度
   * @param {number} speedRatio 速度比率
   */
  async lookTo (tlon, tlat, speedRatio = 1) {
    const cam = this.cameraInstance
    if (tlon - cam.lon > 180) tlon -= 360
    if (tlon - cam.lon < -180) tlon += 360
    const a = {
      lon: cam.lon,
      lat: cam.lat
    }
    this.route = new TWEEN.Tween(a)
    this.route.to({
      lon: tlon,
      lat: tlat
    }, this.transitionTime * speedRatio)
    this.route.onUpdate(() => {
      cam.lon = a.lon
      cam.lat = a.lat
    })
    this.route.easing(TWEEN.Easing.Sinusoidal.InOut)

    return new Promise((resolve, reject) => {
      this.route.onComplete(() => {
        resolve()
      })
      this.route.start()
    })
  }

  /**
   * [TWEEN] 淡入淡出动画
   * @param {number} idx 点位idx
   * @param {number} tlon 目标经度
   * @param {number} tlat 目标纬度
   * @param {function} onInvisible 回调函数
   */
  async fadeTo (idx, tlon, tlat, onInvisible) {
    const cam = this.cameraInstance
    const targetPos = this.cpointSet.getCameraPoint(idx).position

    this.renderer.renderer.domElement.style.opacity = 1
    const dlon = (tlon - this.cameraInstance.lon)
    const dlat = (tlat - this.cameraInstance.lat)
    const delta = new THREE.Vector2(dlon, dlat)
    if (delta.length !== 0) {
      delta.normalize().multiplyScalar(config.PanoMode.FadeRotateDist)
    }

    await this.toOpacity(0, delta.x, delta.y)
    if (this.singleView.nowSingle) { // 这是常规淡入淡出动画，如果当前在单场景2，则需要回到主场景
      this.singleView.exitSingleView()
    }
    onInvisible()
    cam.cameraCurrentIndex = idx
    cam.camera.position.copy(targetPos)
    cam.lon = tlon - delta.x
    cam.lat = tlat - delta.y
    cam.fov = 65
    cam.camera.updateProjectionMatrix()
    await Utils.waitFor(config.PanoMode.FadeRecoverDelay)
    await this.toOpacity(1, delta.x, delta.y)
  }

  /**
   * [TWEEN] 透明度动画
   * @param {number} opacity 透明度
   * @param {number} dlon 目标经度
   * @param {number} dlat 目标纬度
   */
  async toOpacity (opacity, dlon, dlat) {
    const cam = this.cameraInstance
    const a = {
      opacity: this.renderer.renderer.domElement.style.opacity,
      lon: cam.lon,
      lat: cam.lat
    }
    this.fade = new TWEEN.Tween(a)
    this.fade.to({
      opacity: opacity,
      lon: cam.lon + dlon,
      lat: cam.lat + dlat
    }, (config.PanoMode.FadeAllTime - config.PanoMode.FadeRecoverDelay) / 2)
    this.fade.onUpdate(() => {
      this.renderer.renderer.domElement.style.opacity = Math.max(0, a.opacity)
      cam.lon = a.lon
      cam.lat = a.lat
    })
    return new Promise((resolve, reject) => {
      this.fade.onComplete(() => {
        resolve()
      })
      this.fade.start()
    })
  }

  /**
   * 全景到单场景点
   * @param {number} idx 点位idx
   * @param {number} tlon 目标经度
   * @param {number} tlat 目标纬度
   * @param {function} onInvisible 回调函数
   */
  async pano2Single (idx, tlon, tlat, onInvisible) {
    const cam = this.cameraInstance
    const targetPos = config.SingleView.CameraPosition // 单场景2的相机位置，box位置THREE.Vector3(0, 0, 0)

    this.renderer.renderer.domElement.style.opacity = 1
    const dlon = (tlon - cam.lon)
    const dlat = (tlat - cam.lat)
    const delta = new THREE.Vector2(dlon, dlat)
    if (delta.length !== 0) {
      delta.normalize().multiplyScalar(config.PanoMode.FadeRotateDist)
    }

    await this.toOpacity(0, delta.x, delta.y)
    // 变换scene+相机+隐藏objects=[]
    // 贴图变化,根据参数idx来决定加载哪个贴图
    onInvisible()

    this.singleView.enterSingleView() // 这里是变换到单场景2的方法，此时主场景和射线检测列表被暂存
    // cam.cameraCurrentIndex = idx
    cam.camera.position.copy(targetPos)
    cam.lon = tlon - delta.x
    cam.lat = tlat - delta.y
    cam.camera.fov = 65
    cam.camera.updateProjectionMatrix()
    await Utils.waitFor(config.PanoMode.FadeRecoverDelay)

    await this.toOpacity(1, delta.x, delta.y)
  }
}
